import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Alert, Divider, Chip, Paper,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import HealthPassCard, { PassRecord } from '../components/TravelerScreening/HealthPassCard';
import { travelerScreeningAPI } from '../services/api';
import { formatPortName } from '../utils/formatPortName';

const EBOLA_SYMPTOM_LABELS: Record<string, string> = {
  headache: 'Headache',
  muscle_joint_pain: 'Muscle / Joint Pain',
  vomiting_nausea: 'Vomiting / Nausea',
  diarrhoea: 'Diarrhoea',
  abdominal_pain: 'Abdominal Pain',
  bleeding_bruising: 'Unusual Bleeding / Bruising',
  haemorrhagic_rash: 'Haemorrhagic Rash',
  conjunctival: 'Conjunctival Redness',
  sore_throat: 'Sore Throat',
  difficulty_swallowing: 'Difficulty Swallowing',
  altered_consciousness: 'Altered Consciousness',
};

const EBOLA_EXPOSURE_LABELS: Record<string, string> = {
  healthcare_lab_work: 'Healthcare / Lab Work',
  funeral_body_prep: 'Funeral / Body Preparation',
  contact_animals: 'Contact with Animals',
  bushmeat_handling: 'Bushmeat Handling',
  forest_cave_exposure: 'Forest / Cave Exposure',
};


const fmt = (val: any) => {
  if (val === null || val === undefined || val === '') return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  return String(val);
};

const fmtDate = (val: any) => {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleDateString('en-GB', {
      timeZone: 'Africa/Nairobi',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }
  catch { return String(val); }
};

const SectionTitle: React.FC<{ label: string }> = ({ label }) => (
  <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 1, color: '#555', mt: 2.5, mb: 1 }}>
    {label}
  </Typography>
);

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', borderBottom: '1px solid #f0f0f0', py: 0.8 }}>
    <Typography sx={{ width: '45%', fontSize: '0.8rem', color: '#777', fontWeight: 500 }}>{label}</Typography>
    <Typography sx={{ flex: 1, fontSize: '0.85rem', fontWeight: 600, color: '#111' }}>{value}</Typography>
  </Box>
);

const TravelerScreeningPassPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [screening, setScreening]     = useState<any>(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [tempDialog, setTempDialog]   = useState(false);
  const [tempValue, setTempValue]     = useState('');
  const [tempSaving, setTempSaving]   = useState(false);
  const [tempError, setTempError]     = useState<string | null>(null);
  const [tempResult, setTempResult]   = useState<any>(null);
  const [passToken, setPassToken]     = useState<string | null>(null);
  const [clearing, setClearing]       = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [snackbar, setSnackbar]       = useState<string | null>(null);

  const loadScreening = () => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      travelerScreeningAPI.getScreening(Number(id)),
      travelerScreeningAPI.getPassToken(Number(id)).catch(() => null),
    ]).then(([data, tokenData]: any) => {
      setScreening(data);
      if (tokenData?.token) setPassToken(tokenData.token);
      setLoading(false);
    }).catch(() => { setError('Record not found or you do not have access.'); setLoading(false); });
  };

  useEffect(() => { loadScreening(); }, [id]);

  const handleTempSave = async () => {
    const temp = parseFloat(tempValue);
    if (isNaN(temp) || temp < 30 || temp > 45) {
      setTempError('Enter a valid temperature between 30 and 45 °C');
      return;
    }
    setTempSaving(true);
    setTempError(null);
    try {
      const result = await travelerScreeningAPI.updateTemperature(Number(id), temp);
      setTempResult(result);
      setTempSaving(false);
      loadScreening();
    } catch {
      setTempError('Failed to update temperature. Please try again.');
      setTempSaving(false);
    }
  };

  const handleClearForTravel = async () => {
    if (!id) return;
    setClearing(true);
    try {
      await travelerScreeningAPI.clearForTravel(Number(id));
      setSnackbar('Pass updated — cleared for travel.');
      loadScreening();
    } catch {
      setSnackbar('Failed to clear for travel.');
    } finally {
      setClearing(false);
    }
  };

  const handleSendReport = async () => {
    if (!id) return;
    setSendingReport(true);
    try {
      const res = await travelerScreeningAPI.sendReportWhatsapp(Number(id));
      setSnackbar(res?.message || 'Request sent.');
    } catch {
      setSnackbar('Failed to send report.');
    } finally {
      setSendingReport(false);
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <CircularProgress />
    </Box>
  );

  if (error || !screening) return (
    <Box sx={{ p: 4 }}>
      <Alert severity="error">{error || 'Record not found.'}</Alert>
    </Box>
  );

  const pass = screening.pass_record;
  const passRecord: PassRecord | null = pass ? {
    pass_type:          pass.pass_type,
    full_name:          pass.full_name,
    id_number:          pass.id_number,
    country_of_origin:  screening.country_of_origin,
    screening_type:     pass.screening_type,
    screening_status:   pass.screening_status,
    action_required:    pass.action_required,
    next_step:          pass.next_step,
    issued_at:          pass.issued_at,
    valid_until:        pass.valid_until,
    id:                 pass.id,
    screening_id:       screening.id,
    pass_token:         passToken ?? undefined,
  } : null;

  const isEbola = screening.source === 'whatsapp';
  const ebolaData = screening.ebola_screening_data || {};

  const symptoms = isEbola
    ? ((ebolaData.symptoms || []) as string[])
        .filter((s) => s && s !== 'none')
        .map((s) => EBOLA_SYMPTOM_LABELS[s] || s)
    : [
        screening.symptom_fever     && 'Fever',
        screening.symptom_cough     && 'Cough',
        screening.symptom_breathing && 'Difficulty Breathing',
        screening.symptom_vomiting  && 'Vomiting',
        screening.symptom_diarrhea  && 'Diarrhea',
        screening.symptom_jaundice  && 'Jaundice',
        screening.symptom_rash      && 'Rash',
        screening.symptom_bleeding  && 'Bleeding',
      ].filter(Boolean) as string[];

  const exposures = Object.entries(EBOLA_EXPOSURE_LABELS)
    .filter(([key]) => ebolaData[key])
    .map(([, label]) => label);

  const isCleared = screening.cleared_for_travel || pass?.pass_type === 'cleared';

  return (
    <Box sx={{ maxWidth: 780, mx: 'auto', px: 2, py: 3 }}>

      {/* Header row: back + edit temperature */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', color: '#555' }}
          onClick={() => navigate('/dashboard/traveler-screening')}
        >
          <ArrowBackIcon fontSize="small" />
          <Typography sx={{ fontSize: '0.85rem' }}>Back to Screening List</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ThermostatIcon />}
            onClick={() => { setTempValue(screening.temperature ?? ''); setTempResult(null); setTempDialog(true); }}
            sx={{ borderColor: '#1f93ff', color: '#1f93ff' }}
          >
            Edit Temperature
          </Button>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={clearing ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
            disabled={clearing || isCleared}
            onClick={handleClearForTravel}
          >
            {isCleared ? 'Cleared for Travel' : 'Generate Clear for Travel'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={sendingReport ? <CircularProgress size={16} color="inherit" /> : <WhatsAppIcon />}
            disabled={sendingReport}
            onClick={handleSendReport}
          >
            Send Report to WhatsApp
          </Button>
        </Box>
      </Box>

      {/* Health Pass Card */}
      {passRecord ? (
        <HealthPassCard passRecord={passRecord} />
      ) : (
        <Alert severity="warning" sx={{ mb: 2 }}>No health pass generated for this screening.</Alert>
      )}

      {/* Temperature Edit Dialog */}
      <Dialog open={tempDialog} onClose={() => setTempDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ThermostatIcon sx={{ color: '#1f93ff' }} /> Edit Temperature
        </DialogTitle>
        <DialogContent>
          {tempResult ? (
            <Box>
              <Alert severity={tempResult.risk_level === 'high' ? 'error' : 'success'} sx={{ mb: 1 }}>
                Temperature updated — Risk level: <strong>{tempResult.risk_level?.toUpperCase()}</strong>
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Temperature: {tempResult.temperature} °C &nbsp;|&nbsp; Screening ID: {tempResult.screening_id}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              <TextField
                label="Temperature (°C)"
                type="number"
                inputProps={{ step: 0.1, min: 30, max: 45 }}
                value={tempValue}
                onChange={e => setTempValue(e.target.value)}
                fullWidth
                size="small"
                helperText="Normal: 36–37.5 °C  |  Fever: ≥ 38 °C"
                error={!!tempError}
              />
              {tempError && <Alert severity="error" sx={{ mt: 1 }}>{tempError}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTempDialog(false)}>Close</Button>
          {!tempResult && (
            <Button variant="contained" onClick={handleTempSave} disabled={tempSaving}>
              {tempSaving ? 'Saving...' : 'Save & Recalibrate'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Full Q&A Details */}
      <Paper variant="outlined" sx={{ mt: 3, p: 3, borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>Full Screening Details</Typography>
        <Divider sx={{ mb: 1 }} />

        {isEbola ? (
          <>
            <SectionTitle label="Travel Information" />
            <Row label="Country of Departure" value={fmt(screening.country_of_origin)} />
            <Row label="Point of Entry"        value={screening.entry_point_title || fmt(screening.entry_city && formatPortName(screening.entry_city))} />
            <Row label="Mode of Transport"     value={fmt(ebolaData.mode_of_transport)} />
            <Row label="Vessel / Flight No."   value={fmt(screening.transport_number)} />

            <SectionTitle label="Travel History" />
            <Row label="Visited Affected Countries"  value={screening.visited_epidemic_area ? 'Yes' : 'No'} />
            <Row label="Contact with Confirmed Case" value={screening.contact_with_sick ? 'Yes' : 'No'} />
            <Box sx={{ display: 'flex', borderBottom: '1px solid #f0f0f0', py: 0.8 }}>
              <Typography sx={{ width: '45%', fontSize: '0.8rem', color: '#777', fontWeight: 500 }}>Other Exposure</Typography>
              <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {exposures.length > 0
                  ? exposures.map(e => <Chip key={e} label={e} size="small" color="warning" variant="outlined" sx={{ fontSize: '0.72rem' }} />)
                  : <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>None</Typography>
                }
              </Box>
            </Box>

            <SectionTitle label="Health Assessment" />
            <Row label="Temperature (°C)" value={fmt(screening.temperature)} />
            <Box sx={{ display: 'flex', borderBottom: '1px solid #f0f0f0', py: 0.8 }}>
              <Typography sx={{ width: '45%', fontSize: '0.8rem', color: '#777', fontWeight: 500 }}>Symptoms</Typography>
              <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {symptoms.length > 0
                  ? symptoms.map(s => <Chip key={s} label={s} size="small" color="error" variant="outlined" sx={{ fontSize: '0.72rem' }} />)
                  : <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>None</Typography>
                }
              </Box>
            </Box>
          </>
        ) : (
          <>
            <SectionTitle label="Entry Information" />
            <Row label="Entry Type"           value={fmt(screening.entry_type)} />
            <Row label="County / Point of entry" value={screening.entry_point_title || fmt(screening.entry_city && formatPortName(screening.entry_city))} />
            <Row label="Country of departure" value={fmt(screening.country_of_origin)} />
            <Row
              label="Arrival Date"
              value={fmtDate(screening.arrival_date || screening.created_at)}
            />
            <Row label="Arrival Time"         value={fmt(screening.arrival_time)} />
            <Row label="Transport Number"     value={fmt(screening.transport_number)} />
            <Row label="Countries Visited (21d)" value={(screening.countries_visited_21d || []).join(', ') || '—'} />
            <Row label="Travel Purpose"       value={fmt(screening.travel_purpose)} />
            <Row label="Residence in Host Country" value={fmt(screening.residence_host_country)} />

            <SectionTitle label="Personal Details" />
            <Row label="Full Name"      value={fmt(screening.full_name)} />
            <Row label="Sex"            value={fmt(screening.sex)} />
            <Row label="Date of Birth"  value={fmtDate(screening.date_of_birth)} />
            <Row label="Age"            value={fmt(screening.age)} />
            <Row label="Nationality"    value={fmt(screening.nationality)} />
            <Row label="ID Type"        value={fmt(screening.id_type)} />
            <Row label="ID / Passport"  value={fmt(screening.id_number)} />
            <Row label="Phone Number"   value={fmt(screening.phone_number)} />
            <Row label="Email"          value={fmt(screening.email)} />

            <SectionTitle label="Health Assessment" />
            <Row label="Temperature (°C)"      value={fmt(screening.temperature)} />
            <Box sx={{ display: 'flex', borderBottom: '1px solid #f0f0f0', py: 0.8 }}>
              <Typography sx={{ width: '45%', fontSize: '0.8rem', color: '#777', fontWeight: 500 }}>Symptoms Reported</Typography>
              <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {symptoms.length > 0
                  ? symptoms.map(s => <Chip key={s} label={s} size="small" color="error" variant="outlined" sx={{ fontSize: '0.72rem' }} />)
                  : <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111' }}>None</Typography>
                }
              </Box>
            </Box>
            <Row label="Other Symptoms"        value={fmt(screening.symptom_other)} />
            <Row label="Contact with Sick Person"  value={screening.contact_with_sick ? 'Yes' : 'No'} />
            <Row label="Visited Epidemic Area" value={screening.visited_epidemic_area ? 'Yes' : 'No'} />
            <Row label="Epidemic Details"      value={fmt(screening.epidemic_details)} />

            <SectionTitle label="Vaccination" />
            <Row label="Yellow Fever Vaccinated" value={screening.yellow_fever_vaccinated ? 'Yes' : 'No'} />
            <Row label="Yellow Fever Date"       value={fmtDate(screening.yellow_fever_date)} />
            <Row label="Other Vaccinations"      value={fmt(screening.other_vaccinations)} />
          </>
        )}

        <SectionTitle label="Risk Assessment" />
        <Box sx={{ display: 'flex', borderBottom: '1px solid #f0f0f0', py: 0.8 }}>
          <Typography sx={{ width: '45%', fontSize: '0.8rem', color: '#777', fontWeight: 500 }}>Risk Level</Typography>
          <Chip
            label={screening.risk_level || '—'}
            size="small"
            color={screening.risk_level === 'high' ? 'error' : screening.risk_level === 'moderate' ? 'warning' : 'success'}
            sx={{ fontWeight: 700, textTransform: 'uppercase' }}
          />
        </Box>
        {screening.risk_factors && Object.keys(screening.risk_factors).length > 0 && (
          <Box sx={{ display: 'flex', borderBottom: '1px solid #f0f0f0', py: 0.8 }}>
            <Typography sx={{ width: '45%', fontSize: '0.8rem', color: '#777', fontWeight: 500 }}>Risk Factors</Typography>
            <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {Object.entries(screening.risk_factors).map(([k, v]) =>
                v ? <Chip key={k} label={k.replace(/_/g, ' ')} size="small" color="warning" variant="outlined" sx={{ fontSize: '0.72rem' }} /> : null
              )}
            </Box>
          </Box>
        )}
        <Row label="Screened At" value={fmtDate(screening.created_at)} />
      </Paper>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3500}
        onClose={() => setSnackbar(null)}
        message={snackbar || ''}
      />
    </Box>
  );
};

export default TravelerScreeningPassPage;
